import React, { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { Link, useHistory } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import { Badge, Button, Card, CardBody, CardHeader, Col, Input, Row, Table } from 'reactstrap'
import { ToastContainer } from 'react-toastify'
import Header from '../../components/Headers/Header'
import { notify } from '../../utils/notify'
import { api } from '../../utils'
import TagGeneratedQuestionsModal from './TagGeneratedQuestionsModal'
import './LLMVisibilityWorkflow.css'

const LLMVisibilityListing = () => {
  const history = useHistory()
  const itemPerPage = 10

  const [pages, setPages] = useState(0)
  const [tableData, setTableData] = useState([])
  const [actualPage, setActualPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [taggingToolReports, setTaggingToolReports] = useState([])
  const [selectedRun, setSelectedRun] = useState(null)
  const [isTaggingModalOpen, setIsTaggingModalOpen] = useState(false)
  const [isSubmittingTagging, setIsSubmittingTagging] = useState(false)
  const [taggingMode, setTaggingMode] = useState('CLONE')
  const [selectedTaggingReportId, setSelectedTaggingReportId] = useState('')
  const [taggingOptions, setTaggingOptions] = useState({
    forceRegenerateQuestions: false,
    forceNewClone: false,
    forceRetag: false,
  })

  useEffect(() => {
    api
      .get('tagging-tool/reports/')
      .then((response) => setTaggingToolReports(response.data || []))
      .catch(() => notify('Impossibile caricare i report del Tagging Tool.', 'warning'))
  }, [])

  const loadPage = async (page = actualPage) => {
    setLoading(true)
    setError(false)
    try {
      const params = {
        page_size: itemPerPage,
        page,
      }
      const normalizedSearchTerm = debouncedSearchTerm.trim()
      if (normalizedSearchTerm) {
        params.search = normalizedSearchTerm
      }
      if (statusFilter !== 'all') {
        params.status = statusFilter
      }

      const response = await api.get('llm-visibility/', { params })
      setTableData(response.data.results || [])
      setPages(Math.ceil((response.data.count || 0) / itemPerPage))
    } catch (requestError) {
      setError(true)
      setTableData([])
      setPages(0)
      console.error(requestError)
    } finally {
      setLoading(false)
    }
  }

  const handlePageClick = (pageData) => {
    const currentPage = pageData.selected + 1
    setActualPage(currentPage)
    loadPage(currentPage).catch((requestError) => console.error(requestError))
  }

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(debounceTimeout)
  }, [searchTerm])

  useEffect(() => {
    setActualPage(1)
    loadPage(1).catch((requestError) => console.error(requestError))
  }, [debouncedSearchTerm, statusFilter])

  const getStatusBadge = (status) => {
    switch (status) {
      case 'EXPORTED':
        return 'success'
      case 'EXPORTED_QUESTIONS':
        return 'info'
      case 'ERROR':
        return 'danger'
      case 'CREATED':
        return 'primary'
      default:
        return 'warning'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'EXPORTED':
        return 'ESPORTATO'
      case 'EXPORTED_QUESTIONS':
        return 'ESPORTATO (SOLO DOMANDE)'
      case 'ERROR':
        return 'ERRORE'
      case 'CREATED':
        return 'BOZZA'
      case 'RETRIEVING_QUESTIONS':
        return 'GENERAZIONE DOMANDE'
      case 'RETRIEVING_ANSWERS':
        return 'GENERAZIONE RISPOSTE'
      default:
        return status
    }
  }

  const filteredData = tableData

  const getRunType = (status) => {
    if (status === 'EXPORTED_QUESTIONS') return 'Solo domande'
    return 'Report completo'
  }

  const findTaggingReport = (reportId) => taggingToolReports.find((report) => report.id === reportId)

  const getTaggingState = (item, taggingReport) => {
    if (item.status === 'ERROR') {
      return {
        label: 'Bloccato da un errore',
        detail: "Riprendi l'esecuzione prima di avviare altro tagging.",
        actionLabel: 'Apri',
        actionColor: 'warning',
      }
    }

    if (item.status === 'EXPORTED_QUESTIONS' && !taggingReport) {
      return {
        label: 'Pronto per il tagging',
        detail: 'Le domande sono esportate. Scegli ora il flusso del Tagging Tool.',
        actionLabel: 'Tagga domande',
        actionColor: 'info',
      }
    }

    if (taggingReport?.status === 'EXPORTED') {
      return {
        label: 'Collegato ed esportato',
        detail: 'Le viste collegate possono appoggiarsi a questo report di tagging.',
        actionLabel: 'Gestisci tagging',
        actionColor: 'secondary',
      }
    }

    if (taggingReport) {
      return {
        label: 'Collegato, in attesa',
        detail: `Stato report di tagging: ${taggingReport.status || 'Sconosciuto'}.`,
        actionLabel: 'Gestisci tagging',
        actionColor: 'secondary',
      }
    }

    if (item.status === 'EXPORTED') {
      return {
        label: 'Tagging opzionale',
        detail: 'Il report completo è pronto. Aggiungi tagging solo se ti serve arricchimento.',
        actionLabel: 'Tagga domande',
        actionColor: 'info',
      }
    }

    return {
      label: 'Esecuzione in corso',
      detail: "Aspetta l'esportazione prima di avviare il passaggio successivo.",
      actionLabel: 'Apri',
      actionColor: 'default',
    }
  }

  const queueSummary = filteredData.reduce(
    (acc, item) => {
      const taggingReport = findTaggingReport(item.connected_tagging)
      if (item.status === 'EXPORTED_QUESTIONS' && !taggingReport) acc.readyForTagging += 1
      if (taggingReport?.status === 'EXPORTED') acc.linkedAndReady += 1
      if (item.status === 'ERROR') acc.blocked += 1
      return acc
    },
    { readyForTagging: 0, linkedAndReady: 0, blocked: 0 }
  )

  const openTaggingModal = (run) => {
    setSelectedRun(run)
    setSelectedTaggingReportId(run.connected_tagging || '')
    setTaggingMode('CLONE')
    setTaggingOptions({
      forceRegenerateQuestions: false,
      forceNewClone: false,
      forceRetag: false,
    })
    setIsTaggingModalOpen(true)
  }

  const handleTagGeneratedQuestions = async () => {
    if (!selectedRun?.id || !selectedTaggingReportId) {
      notify('Seleziona prima un report del Tagging Tool.', 'warning')
      return
    }

    setIsSubmittingTagging(true)
    try {
      await api.post(`llm-visibility/${selectedRun.id}/tag_generated_questions/`, {
        mode: taggingMode,
        source_tagging_report_id: selectedTaggingReportId,
        force_regenerate_questions: taggingOptions.forceRegenerateQuestions,
        force_new_clone: taggingOptions.forceNewClone,
        force_retag: taggingOptions.forceRetag,
      })
      notify(
        taggingMode === 'CLONE'
          ? 'Il tagging delle domande generate è stato messo in coda.'
          : 'Il report del Tagging Tool è stato collegato.',
        'success'
      )
      setIsTaggingModalOpen(false)
      await loadPage(actualPage)
      const reportsResponse = await api.get('tagging-tool/reports/')
      setTaggingToolReports(reportsResponse.data || [])
    } catch (requestError) {
      notify(requestError.response?.data?.error || 'Impossibile avviare il tagging delle domande generate.', 'error')
    } finally {
      setIsSubmittingTagging(false)
    }
  }

  return (
    <Header
      iconClass="ni ni-align-left-2 text-white"
      title="LLM Visibility"
      description={'Dato un set di parole chiave, genera domande per gli LLM e controlla quanto i brand vengono citati in esse'}
    >
      {loading && (
        <div className="text-center" role="status" aria-live="polite">
          <Spinner animation="grow" />
          <span className="sr-only">Caricamento report LLM Visibility...</span>
        </div>
      )}
      {!loading && error && (
        <Row>
          <Col>
            <Card className="shadow mb-4">
              <CardBody className="text-danger d-flex justify-content-between align-items-center">
                <span>Impossibile caricare i report LLM Visibility.</span>
                <Button color="warning" size="sm" type="button" onClick={() => loadPage(1)}>
                  Riprova
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      {!error && tableData && (
        <Row style={{ marginBottom: '100px' }}>
          <div className="col pb-5">
            <Card className="shadow">
              <TagGeneratedQuestionsModal
                isOpen={isTaggingModalOpen}
                toggle={() => setIsTaggingModalOpen(!isTaggingModalOpen)}
                reports={taggingToolReports}
                mode={taggingMode}
                onModeChange={setTaggingMode}
                selectedReportId={selectedTaggingReportId}
                onSelectedReportChange={setSelectedTaggingReportId}
                options={taggingOptions}
                onOptionsChange={setTaggingOptions}
                onSubmit={handleTagGeneratedQuestions}
                submitting={isSubmittingTagging}
                connectedReport={findTaggingReport(selectedRun?.connected_tagging)}
                tooltipPrefix={`llm-visibility-list-${selectedRun?.id || 'none'}`}
              />
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col xs="12" lg="7">
                    <h1 className="mb-0">Report LLM Visibility</h1>
                    <p className="text-muted mb-0 mt-2">
                      Tratta questa vista come una coda di lavoro: individua le esecuzioni pronte per il tagging, bloccate o già collegate a un report del Tagging Tool.
                    </p>
                  </Col>
                  <Col className="text-right mt-3 mt-lg-0" xs="12" lg="5">
                    <Button
                      className="mr-2"
                      color="secondary"
                      onClick={() => history.push('/llm-visibility-scheduled')}
                      size="md"
                    >
                      Vista schedulata
                    </Button>
                    <Button
                      color="info"
                      onClick={() => history.push('/add-llm-visibility/')}
                      size="md"
                    >
                      + Nuovo report
                    </Button>
                  </Col>
                </Row>
                <div className="llm-visibility-queue-note">
                  <p className="llm-visibility-queue-note__summary">
                    <strong>{filteredData.length}</strong> esecuzioni visibili.
                    {' '}Dai priorità a quelle già pronte per il tagging prima di aprire il resto.
                  </p>
                  <div className="llm-visibility-queue-note__chips">
                    <span className="llm-visibility-chip">{queueSummary.readyForTagging} pronte per il tagging</span>
                    <span className="llm-visibility-chip">{queueSummary.linkedAndReady} collegate ed esportate</span>
                    <span className="llm-visibility-chip">{queueSummary.blocked} bloccate</span>
                  </div>
                </div>
                <Row className="mt-3">
                  <Col md="6">
                    <label htmlFor="llm-visibility-search" className="form-control-label">
                      Cerca report
                    </label>
                    <Input
                      id="llm-visibility-search"
                      type="text"
                      placeholder="Cerca per nome del report"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                  </Col>
                  <Col md="6">
                    <label htmlFor="llm-visibility-status-filter" className="form-control-label">
                      Filtra per stato
                    </label>
                    <Input
                      id="llm-visibility-status-filter"
                      type="select"
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                    >
                      <option value="all">Tutti gli stati</option>
                      <option value="EXPORTED">Esportato</option>
                      <option value="EXPORTED_QUESTIONS">Esportato (solo domande)</option>
                      <option value="ERROR">Errore</option>
                      <option value="CREATED">Bozza</option>
                      <option value="RETRIEVING_QUESTIONS">Generazione domande</option>
                      <option value="RETRIEVING_ANSWERS">Generazione risposte</option>
                    </Input>
                  </Col>
                </Row>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Nome</th>
                    <th scope="col">Data</th>
                    <th scope="col">Tipo</th>
                    <th scope="col">Flusso di tagging</th>
                    <th scope="col">Stato</th>
                    <th scope="col">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        Nessun report trovato con i filtri correnti.
                      </td>
                    </tr>
                  )}
                  {filteredData.map((item) => {
                    const taggingReport = findTaggingReport(item.connected_tagging)
                    const taggingState = getTaggingState(item, taggingReport)

                    return (
                      <tr key={item.id}>
                        <th scope="row">
                          <Link to={`/add-llm-visibility/${item.id}`}>{item.name}</Link>
                        </th>
                        <td>{new Date(item.created).toLocaleString()}</td>
                        <td>
                          <Badge color={item.status === 'EXPORTED_QUESTIONS' ? 'primary' : 'dark'} pill className="text-white">
                            {getRunType(item.status)}
                          </Badge>
                        </td>
                        <td>
                          <div className="llm-visibility-tagging-state">
                            <div className="llm-visibility-tagging-state__label">{taggingState.label}</div>
                            <div className="llm-visibility-tagging-state__detail">{taggingState.detail}</div>
                            {item.connected_tagging && (
                              <Link to={`/reporting/reports/seo/${item.connected_tagging}`}>
                                {taggingReport?.name || `Report ${item.connected_tagging}`}
                              </Link>
                            )}
                          </div>
                        </td>
                        <td>
                          <Badge color={getStatusBadge(item.status)} pill className="text-white">
                            {getStatusLabel(item.status)}
                          </Badge>
                        </td>
                        <td>
                          <div className="llm-visibility-table-action">
                            <Button
                              color={taggingState.actionColor}
                              size="sm"
                              onClick={() => (
                                taggingState.actionLabel === 'Apri'
                                  ? history.push(`/add-llm-visibility/${item.id}`)
                                  : openTaggingModal(item)
                              )}
                            >
                              {taggingState.actionLabel}
                            </Button>
                            <Button
                              color="default"
                              outline
                              size="sm"
                              onClick={() => history.push(`/add-llm-visibility/${item.id}`)}
                            >
                              Apri esecuzione
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                pageCount={pages}
                forcePage={actualPage - 1}
                onPageChange={handlePageClick}
                containerClassName={'pagination justify-content-center pt-5 pb-3'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            </Card>
          </div>
        </Row>
      )}
      <ToastContainer />
    </Header>
  )
}

export default LLMVisibilityListing
